/**
 * Guarded Web Storage.
 *
 * The prototype is published inside a sandboxed iframe, where touching
 * localStorage or sessionStorage can throw (SecurityError) or be absent. Every
 * read and write in the app goes through these helpers, so a blocked store only
 * loses the preference instead of breaking the page.
 *
 * localStorage holds per-viewer preferences (theme, density, saved views).
 * sessionStorage holds per-tab state (drafts, the demo session).
 */

export type StorageArea = 'local' | 'session';

function area(kind: StorageArea): Storage | null {
  try {
    const store =
      kind === 'local' ? window.localStorage : window.sessionStorage;
    return store ?? null;
  } catch {
    return null;
  }
}

/** Reads a string, or null when it is missing or the store is blocked. */
export function readStorage(
  key: string,
  kind: StorageArea = 'local',
): string | null {
  try {
    return area(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** Writes a string. Returns false when the store is blocked or full. */
export function writeStorage(
  key: string,
  value: string,
  kind: StorageArea = 'local',
): boolean {
  try {
    const store = area(kind);
    if (!store) return false;
    store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads JSON and validates it. `parse` should throw or return undefined for a
 * value of the wrong shape (a zod schema's `parse` works), so stale or
 * hand-edited storage never reaches the UI.
 */
export function readJson<T>(
  key: string,
  parse: (value: unknown) => T | undefined,
  kind: StorageArea = 'local',
): T | undefined {
  const raw = readStorage(key, kind);
  if (raw === null) return undefined;
  try {
    return parse(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

export function writeJson(
  key: string,
  value: unknown,
  kind: StorageArea = 'local',
): boolean {
  try {
    return writeStorage(key, JSON.stringify(value), kind);
  } catch {
    return false;
  }
}

/** Storage keys used across the prototype, in one place so they never collide. */
export const STORAGE_KEYS = {
  theme: 'sao-theme',
  recent: 'sao-recent',
  sidebar: 'sao-sidebar',
  navGroups: 'sao-nav-groups',
  session: 'sao-session',
  tableDensity: (tableId: string) => `table:${tableId}:density`,
  tableColumns: (tableId: string) => `table:${tableId}:columns`,
  savedViews: (storageKey: string) => `views:${storageKey}`,
  sheetWidth: (sheetType: string) => `sheet:${sheetType}:width`,
  draft: (dialog: string, subject: string) => `draft:${dialog}:${subject}`,
} as const;
